import {
  AsyncPipe,
  FormStyle,
  NgFor,
  NgIf,
  TranslationWidth,
  getLocaleDayNames,
} from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import { EventLocationInput } from '@sc/data-access-graphql';
import {
  AgendaEvent,
  AgendaEventType,
  Dossier,
  Fitter,
  RecurrentAgendaEvent,
  RecurrentInstanceAgendaEvent,
  normalizeSpecialClosingReason,
  SPECIAL_CLOSING_REASON_OPTIONS,
  intergerNumPattern,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';
import { DialogService } from '@sc/feature-dialog';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import {
  DossierManagerService,
  RecurrentEventDeleteDialogComponent,
} from '@sc/feature-fitter-shared-libs';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  AppointmentEndTimeValidator,
  ButtonComponent,
  CheckboxComponent,
  IconComponent,
  InputHourComponent,
  InputTextComponent,
  OverlappingDossiersComponent,
  SelectComponent,
  UtilsValidators,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { ConfigType, Dayjs } from 'dayjs';
import { merge } from 'lodash-es';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../services';
import { AgendaEventRecurrenceValidators } from './agenda-event-recurrence-validators';

interface EventForm {
  id: FormControl<string>;
  brandId?: FormControl<string>;
  fitterIdList: FormControl<string[]>;
  type: FormControl<AgendaEventType>;
  title: FormControl<string>;
  startDate: FormControl<string>;
  endDate: FormControl<string>;
  locations: FormControl<('center' | 'outside')[]>;
  assignedResources: FormControl<string[]>;
  comment: FormControl<string>;
  allDay: FormControl<boolean>;
  isRecurrent: FormControl<boolean>;
  recurrence: FormGroup<{
    startDate: FormControl<string>;
    endDate: FormControl<string>;
    hasEnd: FormControl<'true' | 'false'>;
    freq: FormControl<'day' | 'week'>;
    every: FormControl<number>;
    daysOfWeek: FormControl<number[]>;
    daysDeletes: FormControl<
      { startDate: string | Date; endDate: string | Date }[]
    >;
  }>;
}

@Component({
  selector: 'sc-planning-event-form',
  styleUrls: ['./planning-form-event.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FitterNameDisplayService],
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    NgIf,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    InputTextComponent,
    MatInputModule,
    MatDatepickerModule,
    InputHourComponent,
    MatRadioModule,
    IconComponent,
    MatExpansionModule,
    AsyncPipe,
    TranslateModule,
    OverlappingDossiersComponent,
    CheckboxComponent,
    SelectComponent,
  ],
  template: `
    <ng-container [formGroup]="form">
      <sc-button
        id="closeBtn"
        class="close-btn mb-l"
        buttonType="secondary"
        (click)="closeEvent.emit($event)"
        leftIcon="close"
        >{{ 'PlanningFormEvent.closeBtn' | translate }}
      </sc-button>

      <div class="event-title mb-l">
        <h2 *ngIf="form.get('type').value === 'GENERIC'">
          {{ 'PlanningFormEvent.informationTitle' | translate }}
        </h2>
        <h2 *ngIf="form.get('type').value === 'SPECIAL_CLOSING'">
          {{ 'PlanningFormEvent.informationTitleClosing' | translate }}
        </h2>
        <h2 *ngIf="eventIsUnavailability()">
          {{ 'PlanningFormEvent.unavailabilityTitle' | translate }}
        </h2>
      </div>

      <ng-container *ngIf="fitters.length > 1 && !event?.id && !isLeaveType()">
        <mat-label class="text-grey-dark font-family-main font-s asterisk">{{
          'PlanningFormEvent.garageSelectLabel' | translate
        }}</mat-label>
        <mat-select
          formControlName="fitterIdList"
          class="resources mb-l sc-design-text-3"
          multiple
          ><mat-select-trigger>
            {{
              getFitterSelectedValue(form.get('fitterIdList')?.value) | async
            }}
          </mat-select-trigger>
          <mat-option *ngFor="let fitter of fitters" [value]="fitter.id">{{
            getFitterSelectedValue([fitter.id]) | async
          }}</mat-option>
        </mat-select>
      </ng-container>
      <sc-select
        class="centers mb-l"
        *ngIf="event?.id || isLeaveType()"
        [value]="form.get('fitterIdList')?.value[0]"
        optionKey="id"
        [options]="fitters"
        (valueChange)="onFitterSelect($event)"
      >
        <ng-template #triggerTemplate let-item>
          <ng-container *ngIf="form.get('fitterIdList')?.value">{{
            getFitterSelectedValue(form.get('fitterIdList')?.value) | async
          }}</ng-container>
        </ng-template>

        <ng-template #optionTemplate let-item>
          <ng-container *ngIf="item?.id">{{
            getFitterSelectedValue(item?.id) | async
          }}</ng-container>
        </ng-template>
      </sc-select>

      <ng-container
        *ngIf="
          eventIsUnavailability() &&
          form.get('fitterIdList')?.value?.length <= 1
        "
      >
        <mat-label class="text-grey-dark font-family-main font-s asterisk">
          <label for="eventType-select">
            {{ 'PlanningFormEvent.resourceTypeLabel' | translate }}
          </label>
        </mat-label>
        <sc-select
          id="eventType-select"
          class="eventTypeSelect mb-l"
          formControlName="type"
          [options]="leaveList"
          optionKey="key"
          (valueChange)="form.get('assignedResources').setValue(null)"
        >
          <ng-template #triggerTemplate let-item>
            {{ getCurrentEventTypeName() | translate }}
          </ng-template>

          <ng-template #optionTemplate let-item>
            {{ item.name | translate }}
          </ng-template>
        </sc-select>
      </ng-container>

      <div
        class="resource-selection-container mb-l"
        *ngIf="
          eventIsUnavailability() &&
          form.get('fitterIdList')?.value?.length <= 1
        "
      >
        <mat-label
          *ngIf="form.get('type').value === 'TECH_LEAVE'"
          class="font-s text-grey-dark asterisk"
          >{{ 'PlanningFormEvent.resourceLabel' | translate }}</mat-label
        >
        <mat-label
          *ngIf="form.get('type').value === 'INTERVENTION_VEHICLE_LEAVE'"
          class="font-s text-grey-dark asterisk"
          >{{
            'PlanningFormEvent.interventionVehicleLabel' | translate
          }}</mat-label
        >
        <mat-label
          *ngIf="
            form.get('type').value === 'RV_LEAVE' &&
            form.get('fitterIdList')?.value?.length <= 1
          "
          class="font-s text-grey-dark asterisk"
          >{{ 'PlanningFormEvent.vehicleLabel' | translate }}</mat-label
        >
        <mat-select
          multiple
          *ngIf="
            form.get('type')?.value === 'TECH_LEAVE' &&
            form.get('fitterIdList')?.value?.length <= 1
          "
          formControlName="assignedResources"
          class="resources sc-design-text-3"
        >
          <mat-option
            *ngFor="let resource of getFitterResources()"
            [value]="resource"
            >{{ resource }}</mat-option
          >
        </mat-select>

        <mat-select
          multiple
          *ngIf="
            form.get('type')?.value === 'INTERVENTION_VEHICLE_LEAVE' &&
            form.get('fitterIdList')?.value?.length <= 1
          "
          formControlName="assignedResources"
          class="resources sc-design-text-3"
        >
          <mat-option
            *ngFor="let resource of getFitterInterventionVehicles()"
            [value]="resource"
            >{{ resource }}</mat-option
          >
        </mat-select>

        <mat-select
          *ngIf="
            form.get('type')?.value === 'RV_LEAVE' &&
            form.get('fitterIdList')?.value?.length <= 1
          "
          formControlName="assignedResources"
          class="resources sc-design-text-3"
          multiple
        >
          <mat-option
            *ngFor="let resource of getFitterReplacementVehicles()"
            [value]="resource"
            >{{ resource }}</mat-option
          >
        </mat-select>
      </div>

      <div *ngIf="eventIsExceptionalClosing()">
        <p class="asterisk">{{ 'PlanningFormEvent.reason' | translate }}</p>
        <sc-select formControlName="title" [options]="specialClosingReasons">
          <ng-template #triggerTemplate let-reason>
            {{ translator.getInstantTranslationOrValue(reason) }}
          </ng-template>
          <ng-template #optionTemplate let-reason>
            {{ translator.getInstantTranslationOrValue(reason) }}
          </ng-template>
        </sc-select>
      </div>

      <sc-input-text
        *ngIf="!eventIsExceptionalClosing()"
        formControlName="title"
        [label]="'PlanningFormEvent.title' | translate"
      ></sc-input-text>

      <div class="date-time-selections-container mb-s mt-xxl">
        <h3>{{ 'PlanningFormEvent.beginTitle' | translate }}</h3>
        <div class="date-time-selection">
          <div class="date-selection mr-s">
            <sc-input-text
              appearance="fill"
              rightIcon="arrow_bottom"
              [label]="'PlanningFormEvent.dayLabel' | translate"
              [asterisk]="true"
              [clickableIcon]="true"
              [value]="getFormattedDate('startDate', 'DD/MM/YYYY')"
              (focus)="inputDatePickerStart.click()"
              (click)="inputDatePickerStart.click()"
              (input)="onStartDateChange($event)"
            ></sc-input-text>
            <input
              class="hidden-input"
              #inputDatePickerStart
              matInput
              [matDatepicker]="pickerStart"
              (focus)="pickerStart.open()"
              (click)="pickerStart.open()"
              (dateChange)="onStartDateChange($event)"
            />
            <mat-datepicker #pickerStart></mat-datepicker>
          </div>
          <div class="time-selection ml-s">
            <sc-input-hour
              formControlName="startDate"
              [label]="'PlanningFormEvent.hourLabel' | translate"
              [timezone]="getTimezone()"
            ></sc-input-hour>
          </div>
        </div>
        <h3>{{ 'PlanningFormEvent.endTitle' | translate }}</h3>
        <div class="date-time-selection">
          <div class="date-selection mr-s">
            <sc-input-text
              appearance="fill"
              rightIcon="arrow_bottom"
              [label]="'PlanningFormEvent.dayLabel' | translate"
              [asterisk]="true"
              [clickableIcon]="true"
              [value]="getFormattedDate('endDate', 'DD/MM/YYYY')"
              (focus)="inputDatePickerEnd.click()"
              (click)="inputDatePickerEnd.click()"
              (input)="onEndDateChange($event)"
            ></sc-input-text>
            <input
              class="hidden-input"
              #inputDatePickerEnd
              matInput
              [matDatepicker]="pickerEnd"
              [min]="getFormattedMomentDate('startDate')"
              (focus)="pickerEnd.open()"
              (click)="pickerEnd.open()"
              (dateChange)="onEndDateChange($event)"
            />
            <mat-datepicker #pickerEnd></mat-datepicker>
          </div>
          <div class="time-selection ml-s">
            <sc-input-hour
              formControlName="endDate"
              [label]="'PlanningFormEvent.hourLabel' | translate"
              [timezone]="getTimezone()"
              [_minDate]="form.get('startDate')?.value"
            ></sc-input-hour>
          </div>
        </div>
        <div class="time-error" *ngIf="touched && !areTimeSelected()">
          {{ 'PlanningFormEvent.dateAndTimeNotFilled' | translate }}
        </div>

        <div class="time-error" *ngIf="!isEndTimeValid()">
          {{ 'PlanningFormEvent.endTimeNotValid' | translate }}
        </div>
        <div
          class="alldays"
          *ngIf="form.get('type')?.value === 'SPECIAL_CLOSING'"
        >
          <sc-checkbox
            formControlName="allDay"
            (changeValue)="onChangeDaysChoice()"
          >
            <span class="sc-design-text-3">{{
              'PlanningFormEvent.allDay' | translate
            }}</span>
          </sc-checkbox>
        </div>
      </div>

      <ng-container *ngIf="displayLocationChoice()">
        <mat-label class="text-grey-dark font-family-main font-s asterisk">{{
          'PlanningFormEvent.location' | translate
        }}</mat-label>
        <mat-select
          formControlName="locations"
          class="location mb-l sc-design-text-3"
          multiple
          (selectionChange)="onLocationChange()"
        >
          <mat-option value="center">{{
            'PlanningFormEvent.inCenter' | translate
          }}</mat-option>
          <mat-option value="outside">{{
            'PlanningFormEvent.outside' | translate
          }}</mat-option>
        </mat-select>
      </ng-container>

      <ng-container
        *ngIf="
          displayResourceChoice() &&
          form.get('fitterIdList')?.value?.length <= 1
        "
      >
        <mat-label class="text-grey-dark font-family-main font-s">{{
          'PlanningFormEvent.resourcesLabel' | translate
        }}</mat-label>
        <mat-select
          formControlName="assignedResources"
          class="resources mb-l sc-design-text-3"
          multiple
        >
          <mat-option
            *ngFor="let resource of getFitterResources()"
            [value]="resource"
            >{{ resource }}</mat-option
          >
        </mat-select>
      </ng-container>

      <div class="recurrence-infos-container">
        <sc-checkbox formControlName="isRecurrent">
          <span class="sc-design-text-3">{{
            'PlanningFormEvent.recurrenceCheckboxLabel' | translate
          }}</span>
        </sc-checkbox>

        <div
          *ngIf="!!form.get('isRecurrent')?.value"
          class="recurrence-details-container"
          formGroupName="recurrence"
        >
          <div class="frequency-container">
            <span class="frequecy-label sc-design-text-4-bold">{{
              'PlanningFormEvent.recurrenceLabelEvery'
                | translate
                  : {
                      isDay:
                        form.get('recurrence')?.get('freq')?.value === 'day'
                    }
            }}</span>
            <sc-input-text
              class="frequency-number-input"
              appearance="fill"
              rightIcon="arrow_bottom"
              [clickableIcon]="true"
              type="number"
              formControlName="every"
              [suggestions]="frequecySuggestions"
              (selectSuggestion)="onSelectSuggestionEvery($event)"
            ></sc-input-text>
            <mat-form-field class="frequency-unit-input" appearance="fill">
              <mat-select formControlName="freq" class="sc-design-text-3">
                <mat-option value="day">{{
                  'PlanningFormEvent.recurrenceFreqDay' | translate
                }}</mat-option>
                <mat-option value="week">{{
                  'PlanningFormEvent.recurrenceFreqWeek' | translate
                }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div
            class="week-frequency-container"
            *ngIf="form.get('recurrence')?.get('freq')?.value === 'week'"
          >
            <span class="sc-design-text-4-bold">{{
              'PlanningFormEvent.recurrenceWeekDaysLabel' | translate
            }}</span>
            <div class="days">
              <sc-checkbox
                *ngFor="let day of daysOrder"
                class="weekday-checkbox"
                [value]="isRecurrenceWeekDayChecked(day)"
                (changeValue)="onChangeRecurrenceWeekDay(day)"
              >
                <span class="sc-design-text-3">{{ days[day] }}</span>
              </sc-checkbox>
            </div>
          </div>
          <div class="recurrence-end-container">
            <span class="sc-design-text-4-bold">{{
              'PlanningFormEvent.recurrenceEndDateLabel' | translate
            }}</span>
            <div class="recurrence-end-inputs">
              <mat-radio-group
                class="has-end-radio-group"
                formControlName="hasEnd"
              >
                <mat-radio-button class="has-end-radio-false" value="false"
                  ><span class="sc-design-text-3">{{
                    'PlanningFormEvent.recurrenceHasEndChoiceNever' | translate
                  }}</span></mat-radio-button
                >
                <mat-radio-button class="has-end-radio-true" value="true"
                  ><span class="sc-design-text-3">{{
                    'PlanningFormEvent.recurrenceHasEndChoiceYes' | translate
                  }}</span></mat-radio-button
                >
              </mat-radio-group>

              <sc-input-text
                appearance="fill"
                [disabled]="
                  form.get('recurrence')?.get('hasEnd')?.value === 'false'
                "
                rightIcon="arrow_bottom"
                [clickableIcon]="true"
                [value]="getFormattedRecurrentEndDate()"
                (focus)="inputDatePickerRecurrentEnd.click()"
                (click)="inputDatePickerRecurrentEnd.click()"
                (input)="onRecurrentEndDateChange($any($event))"
              ></sc-input-text>
              <input
                class="hidden-input"
                #inputDatePickerRecurrentEnd
                matInput
                [disabled]="
                  form.get('recurrence')?.get('hasEnd')?.value === 'false'
                "
                [matDatepicker]="pickerRecurrentEnd"
                [min]="getFormattedMomentDate('startDate')"
                (focus)="pickerRecurrentEnd.open()"
                (click)="pickerRecurrentEnd.open()"
                (dateChange)="onRecurrentEndDateChange($any($event))"
              />
              <mat-datepicker #pickerRecurrentEnd></mat-datepicker>
            </div>
          </div>
        </div>
      </div>

      <div
        class="overlap-dossiers-alert-title-container"
        *ngIf="overlapDossiers?.length"
      >
        <sc-icon name="alert"></sc-icon>
        <span class="overlap-dossiers-alert-title">{{
          'PlanningFormEvent.dossierOverlapsClosingAlert' | translate
        }}</span>
      </div>
      <mat-accordion
        *ngIf="overlapDossiers?.length"
        class="overlap-dossier-accordion"
      >
        <mat-expansion-panel [expanded]="false" class="mat-elevation-z">
          <mat-expansion-panel-header>
            <mat-panel-title>
              {{ 'PlanningFormEvent.overlapDossierAccordionTitle' | translate }}
            </mat-panel-title>
          </mat-expansion-panel-header>

          <sc-overlapping-dossiers
            [overlapDossiers]="overlapDossiers"
            [fitter]="fitter"
            [timezone]="fitter?.timezone"
            [isReducedWidth]="true"
            (dossierClick)="onOverlappingDossierClick($event)"
          >
          </sc-overlapping-dossiers>
        </mat-expansion-panel>
      </mat-accordion>

      <h2 *ngIf="form.get('type').value !== 'SPECIAL_CLOSING'" class="mb-l">
        {{ 'PlanningFormEvent.commentTitle' | translate }}
      </h2>
      <textarea
        *ngIf="form.get('type').value !== 'SPECIAL_CLOSING'"
        class="mb-l"
        [placeholder]="'PlanningFormEvent.commentLabel' | translate"
        formControlName="comment"
      ></textarea>

      <sc-button
        *ngIf="displaySaveBtn()"
        id="saveBtn"
        class="mb-l"
        leftIcon="check_bis"
        [disabled]="form?.invalid"
        (click)="saveEvent()"
        >{{ 'PlanningFormEvent.saveButton' | translate }}
      </sc-button>

      <sc-button
        *ngIf="displayDeleteBtn()"
        id="deleteBtn"
        buttonType="error"
        leftIcon="close"
        (click)="deleteEvent()"
        >{{ 'PlanningFormEvent.deleteButton' | translate }}
      </sc-button>
    </ng-container>
  `,
})
export class PlanningFormEventComponent {
  readonly specialClosingReasons = SPECIAL_CLOSING_REASON_OPTIONS;

  @Input() set _fitters(fitters: Fitter[]) {
    this.fitters = fitters;
    this.initEventView();
  }
  @Input() set _event(event: AgendaEvent | RecurrentInstanceAgendaEvent) {
    this.event = event;
    this.initEventView();
  }

  fetchedEvent: AgendaEvent | RecurrentAgendaEvent;
  form: FormGroup<EventForm>;
  event: AgendaEvent | RecurrentInstanceAgendaEvent;
  fitter: Fitter;
  timeSuggestions: string[];
  fitters: Fitter[];
  @Output() closeEvent = new EventEmitter<any>();
  @Output() validateEvent = new EventEmitter<AgendaEvent[]>();

  touched;
  overlapDossiers: Dossier[];

  isEventRecurrent = false;

  frequecySuggestions = new Array<number>(30).fill(0).map((_, i) => i + 1);
  days: ReadonlyArray<string>;
  daysOrder: number[] = [1, 2, 3, 4, 5, 6, 0];

  leaveList: { key: AgendaEventType; name: string }[] = [
    { key: 'TECH_LEAVE', name: 'PlanningFormEvent.resourceLabel' },
    {
      key: 'INTERVENTION_VEHICLE_LEAVE',
      name: 'PlanningFormEvent.interventionVehicleLabel',
    },
    { key: 'RV_LEAVE', name: 'PlanningFormEvent.vehicleLabel' },
  ];

  askOverlapDossiers$ = new Subject<null>();

  userTimezone = dayjs.tz.guess();

  constructor(
    private readonly planningSandbox: PlanningSandboxService,
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly dossierManagerService: DossierManagerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly router: FitterRouterService,
    public readonly translator: TranslatorService,
    public timezoneService: TimezoneService,
    private readonly fitterNameDisplayService: FitterNameDisplayService,
    private readonly dialogService: DialogService
  ) {
    this.translator.getCurrentLang().subscribe((newFormattedLang) => {
      this.days = getLocaleDayNames(
        newFormattedLang,
        FormStyle.Standalone,
        TranslationWidth.Wide
      );
    });

    this.askOverlapDossiers$
      .pipe(debounceTime(1000))
      .subscribe(() => this.updateOverlappingDossiers());
  }

  initEventView() {
    this.timeSuggestions = [];
    for (let i = 7; i < 20; i++) {
      this.timeSuggestions.push(`${i}:00`, `${i}:15`, `${i}:30`, `${i}:45`);
    }

    const locationValidators: ValidatorFn[] = [];
    if (this.displayLocationChoice()) {
      locationValidators.push(Validators.required);
    }

    this.form = new FormGroup<EventForm>(
      {
        id: new FormControl(null),
        fitterIdList: new FormControl([null], [Validators.required]),
        type: new FormControl(null, [Validators.required]),
        title: new FormControl(
          null,
          this.event?.type === 'SPECIAL_CLOSING' ? Validators.required : null
        ),
        startDate: new FormControl(null, [Validators.required]),
        endDate: new FormControl(null, [Validators.required]),
        locations: new FormControl(null, locationValidators),
        assignedResources: new FormControl(
          null,
          this.event?.type === 'RV_LEAVE' ||
          this.event?.type === 'TECH_LEAVE' ||
          this.event?.type === 'INTERVENTION_VEHICLE_LEAVE'
            ? Validators.required
            : null
        ),
        comment: new FormControl(null),
        allDay: new FormControl(null),
        isRecurrent: new FormControl(false),
        recurrence: new FormGroup(
          {
            startDate: new FormControl(null),
            endDate: new FormControl(null),
            hasEnd: new FormControl<EventForm['recurrence']['value']['hasEnd']>(
              'false'
            ),
            freq: new FormControl<EventForm['recurrence']['value']['freq']>(
              'day'
            ),
            every: new FormControl(1, [
              Validators.pattern(intergerNumPattern),
              Validators.min(1),
            ]),
            daysOfWeek: new FormControl(null),
            daysDeletes: new FormControl(null),
          },
          {
            validators: [
              AppointmentEndTimeValidator.consistency('startDate', 'endDate'),
            ],
          }
        ),
      },
      {
        validators: [
          AppointmentEndTimeValidator.consistency('startDate', 'endDate'),
          AgendaEventRecurrenceValidators.endDateCheck(),
        ],
      }
    );

    this.isEventRecurrent =
      this.planningCalendarService.isEventRecurrenceInstance(this.event);

    if (this.event?.id || this.isEventRecurrent) {
      this.planningSandbox
        .fetchEvent(
          (this.event as RecurrentInstanceAgendaEvent)?.generatedFrom ||
            this.event?.id
        )
        .subscribe((event) => {
          this.fetchedEvent = event;
          delete (this.fetchedEvent as RecurrentInstanceAgendaEvent)
            .generatedFrom;
          this.setFormFromEvent(event);
        });
    } else {
      this.setFormFromEvent(this.event);
      if (this.fitters?.length && this.event?.fitterId) {
        this.onFitterSelect(this.event?.fitterId);
      }
    }

    //set center on an event if there is only one
    if (this.fitters?.length === 1) {
      this.onFitterSelect(this.fitters[0].id);
    }
  }

  getCurrentEventTypeName() {
    return this.leaveList.find(
      (leaveItem) => leaveItem.key === this.form.get('type')?.value
    )?.name;
  }

  getTimezone() {
    return this.fitter && this.form?.get('fitterIdList')?.value?.length <= 1
      ? this.timezoneService.getFitterTimezone(this.fitter)
      : this.userTimezone;
  }

  eventIsGeneric() {
    return this.form
      ? this.form.get('type').value === 'GENERIC'
      : this.event?.type === 'GENERIC';
  }
  eventIsExceptionalClosing() {
    return this.form
      ? this.form.get('type').value === 'SPECIAL_CLOSING'
      : this.event?.type === 'SPECIAL_CLOSING';
  }

  eventIsUnavailability() {
    return (
      this.form.get('type').value === 'TECH_LEAVE' ||
      this.form.get('type').value === 'INTERVENTION_VEHICLE_LEAVE' ||
      this.form.get('type').value === 'RV_LEAVE'
    );
  }

  setFormFromEvent(event: AgendaEvent | RecurrentAgendaEvent) {
    this.form.get('id')?.setValue(event?.id);
    this.form.get('brandId')?.setValue(event?.brandId);
    this.form
      .get('fitterIdList')
      ?.setValue(
        event?.fitterId
          ? [event?.fitterId]
          : this.fitters?.length === 1
          ? [this.fitters[0].id]
          : []
      );
    this.form.get('type')?.setValue(event?.type ?? 'GENERIC');
    this.form
      .get('title')
      ?.setValue(
        event?.type === 'SPECIAL_CLOSING'
          ? normalizeSpecialClosingReason(event?.title)
          : event?.title
      );
    this.form.get('startDate')?.setValue(event?.startTime as string);
    this.form.get('endDate')?.setValue(event?.endTime as string);
    this.form.get('allDay')?.setValue(event?.allDay);
    this.form
      .get('locations')
      ?.setValue(
        event?.type === 'SPECIAL_CLOSING' && !event?.id
          ? ['outside', 'center']
          : event?.locations
      );
    this.form
      .get('assignedResources')
      ?.setValue(event?.resources?.map((resource) => resource?.name) ?? []);
    this.form.get('comment')?.setValue(event?.comment);

    const isRecurrent = this.planningCalendarService.isEventRecurrent(event);

    this.form.get('isRecurrent')?.setValue(isRecurrent);

    this.resetAssignedRessources();
    if (isRecurrent) {
      const recurrenceControlGroup = this.form.get('recurrence');

      recurrenceControlGroup
        ?.get('startDate')
        ?.setValue(event?.recurrent?.startDate as string);
      recurrenceControlGroup
        ?.get('endDate')
        ?.setValue(event?.recurrent?.endDate as string);
      recurrenceControlGroup
        ?.get('hasEnd')
        ?.setValue(`${!!event?.recurrent?.endDate}`);
      recurrenceControlGroup?.get('freq')?.setValue(event?.recurrent?.freq);
      recurrenceControlGroup?.get('every')?.setValue(event?.recurrent?.every);

      recurrenceControlGroup
        ?.get('daysOfWeek')
        ?.setValue(event?.recurrent?.daysOfWeek);
    }

    this.onFitterSelect(event?.fitterId);
    this.setSpecialClosingHoursExtreme();
    this.changeDetectorRef.markForCheck();
  }

  generateEventFromForm(
    usedFitter: Fitter
  ): AgendaEvent | RecurrentAgendaEvent {
    const form = this.form.getRawValue();
    const hasMiltipleFitter = form?.fitterIdList?.length > 1;
    let recurrence: RecurrentAgendaEvent['recurrent'] | null = null;

    let daysDeletesList = [];
    if (this.fetchedEvent) {
      daysDeletesList = (this.fetchedEvent as RecurrentAgendaEvent).recurrent
        ?.daysDeletes;
    }

    if (form?.isRecurrent) {
      recurrence = {
        startDate: form?.startDate,
        endDate:
          form?.recurrence?.hasEnd === 'true'
            ? form?.recurrence?.endDate
            : null,
        freq: form?.recurrence?.freq,
        every: +form?.recurrence?.every,
        daysOfWeek:
          form?.recurrence?.freq === 'week'
            ? form?.recurrence?.daysOfWeek || [dayjs(form?.startDate)?.day()]
            : null,
        daysDeletes: daysDeletesList,
      };
    }

    if (this.fetchedEvent) {
      if ((this.fetchedEvent as RecurrentAgendaEvent).recurrent?.daysDeletes) {
        const timezone = this.timezoneService.getFitterTimezone(usedFitter);

        const newStartDate = dayjs(form?.startDate).utc().tz(timezone);
        const newEndDate = dayjs(form?.endDate).utc().tz(timezone);
        let deleteStartDate, deleteEndDate;
        let newStartDateComp,
          deleteStartDateComp,
          newEndDateComp,
          deleteEndDateComp;

        (
          this.fetchedEvent as RecurrentAgendaEvent
        ).recurrent?.daysDeletes.forEach((dayDelete, index) => {
          deleteStartDate = dayjs(dayDelete.startDate).utc().tz(timezone);
          deleteEndDate = dayjs(dayDelete.endDate).utc().tz(timezone);

          newStartDateComp = newStartDate
            .clone()
            .year(newStartDate.year())
            .month(newStartDate.month())
            .date(newStartDate.date());
          deleteStartDateComp = deleteStartDate
            .clone()
            .year(newStartDate.year())
            .month(newStartDate.month())
            .date(newStartDate.date());

          if (!newStartDateComp.isSame(deleteStartDateComp, 'minute')) {
            const date = new Date(recurrence.daysDeletes[index].startDate);

            date.setMinutes(newStartDateComp.toDate().getMinutes());
            date.setHours(newStartDateComp.toDate().getHours());

            recurrence.daysDeletes[index].startDate = date.toISOString();
          }

          newEndDateComp = newEndDate
            .clone()
            .year(newEndDate.year())
            .month(newEndDate.month())
            .date(newEndDate.date());
          deleteEndDateComp = deleteEndDate
            .clone()
            .year(newEndDate.year())
            .month(newEndDate.month())
            .date(newEndDate.date());

          if (!newEndDateComp.isSame(deleteEndDateComp, 'minute')) {
            const date = new Date(recurrence.daysDeletes[index].endDate);

            date.setMinutes(newEndDateComp.toDate().getMinutes());
            date.setHours(newEndDateComp.toDate().getHours());

            recurrence.daysDeletes[index].endDate = date.toISOString();
          }
        });
      }
    }

    return {
      id: form?.id,
      brandId: usedFitter?.brandId,
      fitterId: usedFitter?.id,
      title: form?.title,
      type: form?.type,
      startTime: form?.startDate,
      endTime: form?.endDate,
      locations: form?.locations || [],
      timezone: this.timezoneService.getFitterTimezone(usedFitter),
      resources: hasMiltipleFitter
        ? null
        : form?.assignedResources?.map((res) => {
            return { name: res };
          }) ?? [],
      comment: form?.comment,
      recurrent: recurrence,
    };
  }

  displayDeleteBtn() {
    return !!this.form?.get('id')?.value;
  }

  displaySaveBtn() {
    return true;
  }

  saveEvent() {
    if (this.form.invalid) {
      this.touched = true;
      return;
    }
    const fitterIds = this.form?.getRawValue()?.fitterIdList;

    const eventsToUpdate: AgendaEvent[] = [];
    for (const id of fitterIds) {
      const fitter = this.fitters?.find((fitter) => fitter?.id === id);
      const eventGenerated = this.generateEventFromForm(fitter);

      if (this.isEventTimeValid(eventGenerated)) {
        eventsToUpdate.push(eventGenerated);
      }
    }

    if (!eventsToUpdate.length) {
      return;
    }
    this.validateEvent.emit(eventsToUpdate);
  }

  deleteEvent() {
    let deleteEvent$: Observable<
      'cancel' | 'all' | 'all-next' | 'only-this-event'
    > = of('all');

    if (this.isEventRecurrent && !!this.fetchedEvent) {
      deleteEvent$ = this.dialogService.showDialog(
        RecurrentEventDeleteDialogComponent,
        {},
        'recurrent-event-delete-dialog'
      );
    }

    const actionDeleteEvent$ = deleteEvent$.pipe(
      filter((deleteEvent) => deleteEvent !== 'cancel'),
      take(1)
    );

    actionDeleteEvent$
      .pipe(
        filter((deleteEvent) => deleteEvent === 'all'),
        tap(() => this.loadingService.setLoading(true)),
        switchMap(() =>
          this.planningSandbox.deleteEvent(this.form.get('id').value)
        ),
        take(1)
      )
      .subscribe({
        next: () => {
          this.planningSandbox.updatePlanning();
          this.closeEvent.emit();
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningFormEvent.deleteEventError'
            )
          );

          console.error(error);
        },
      });

    actionDeleteEvent$
      .pipe(
        filter((deleteEvent) => deleteEvent === 'all-next'),
        tap(() => this.loadingService.setLoading(true)),
        map(
          (): RecurrentAgendaEvent => ({
            ...this.fetchedEvent,
            recurrent: {
              ...(this.fetchedEvent as RecurrentAgendaEvent).recurrent,
              endDate: dayjs(this.event.startTime).startOf('day').toDate(),
            },
          })
        ),
        take(1),
        tap((modifiedEvent) => {
          if (!this.isEventTimeValid(modifiedEvent)) {
            throw new Error('PlanningFormEvent.deleteEventError');
          }
        })
      )
      .subscribe({
        next: (modifiedEvent) => {
          this.validateEvent.emit([modifiedEvent]);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningFormEvent.deleteEventError'
            )
          );

          console.error(error);
        },
      });

    actionDeleteEvent$
      .pipe(
        filter((deleteEvent) => deleteEvent === 'only-this-event'),
        tap(() => this.loadingService.setLoading(true)),
        map(
          (): RecurrentAgendaEvent => ({
            ...this.fetchedEvent,
            recurrent: {
              ...(this.fetchedEvent as RecurrentAgendaEvent).recurrent,
              daysDeletes: [
                ...((this.fetchedEvent as RecurrentAgendaEvent).recurrent
                  .daysDeletes || []),
                {
                  startDate: dayjs(this.event.startTime).toDate(),
                  endDate: dayjs(this.event.endTime).toDate(),
                },
              ],
            },
          })
        ),
        take(1),
        tap((modifiedEvent) => {
          if (!this.isEventTimeValid(modifiedEvent)) {
            throw new Error('PlanningFormEvent.deleteEventError');
          }
        })
      )
      .subscribe({
        next: (modifiedEvent) => {
          this.validateEvent.emit([modifiedEvent]);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningFormEvent.deleteEventError'
            )
          );

          console.error(error);
        },
      });
  }

  onLocationChange() {
    this.checkAndUpdateOverlapDossier();
    this.resetAssignedRessources();
  }

  resetAssignedRessources() {
    const assignedResourcesControl = this.form?.get('assignedResources');

    if (
      this.form?.get('locations')?.value?.length > 0 &&
      !this.form?.get('locations')?.value?.includes('center')
    ) {
      assignedResourcesControl?.setValue(null);
      assignedResourcesControl?.disable();
      this.form?.updateValueAndValidity();
    } else {
      assignedResourcesControl?.enable();
    }
  }

  onChangeDaysChoice() {
    this.setSpecialClosingHoursExtreme();
    this.checkAndUpdateOverlapDossier();
  }

  isRecurrenceWeekDayChecked(dayNum: number) {
    return this.form
      .get('recurrence')
      ?.get('daysOfWeek')
      ?.value?.includes?.(dayNum);
  }

  onChangeRecurrenceWeekDay(dayNum: number) {
    const daysControl = this.form.get('recurrence')?.get('daysOfWeek');
    const currentFormList: number[] = daysControl?.value || [];

    if (currentFormList?.includes?.(dayNum)) {
      daysControl?.setValue(currentFormList.filter((day) => day !== dayNum));
    } else {
      daysControl?.setValue([...currentFormList, dayNum].sort());
    }
  }

  checkAndUpdateOverlapDossier() {
    const specialClosingHour = this.form.getRawValue();
    if (
      specialClosingHour?.endDate &&
      specialClosingHour?.startDate &&
      specialClosingHour?.fitterIdList.length &&
      specialClosingHour?.type === 'SPECIAL_CLOSING'
    ) {
      this.askOverlapDossiers$.next(null);
    }
  }

  updateOverlappingDossiers() {
    const specialClosingHour = this.form.getRawValue();

    const periods: {
      startTime: string | Date;
      endTime: string | Date;
    }[] = [];

    if (this.isEventRecurrent) {
      const fitters = this.fitters?.filter((fitter) =>
        specialClosingHour?.fitterIdList?.includes(fitter?.id)
      );
      for (const fitter of fitters) {
        const formEvent = this.generateEventFromForm(fitter);

        const allEventsOfRecurrent =
          this.planningCalendarService.constructAllCalendarGenericEvent(
            formEvent,
            [this.fitters?.find((fitter) => fitter?.id === formEvent.fitterId)],
            {
              start: dayjs(this.planningSandbox.currentTimeFrameStart),
              end: dayjs(this.planningSandbox.currentTimeFrameEnd),
            }
          );
        periods.push(
          ...allEventsOfRecurrent.map((e) => ({
            startTime: e.start.toISOString?.() || e.start,
            endTime: e.end.toISOString?.() || e.end,
          }))
        );
      }
    } else {
      periods.push({
        startTime: dayjs(specialClosingHour.startDate)?.toISOString(),
        endTime: dayjs(specialClosingHour.endDate)?.toISOString(),
      });
    }

    const locations = specialClosingHour.locations?.length
      ? specialClosingHour.locations
      : ['center'];

    this.dossierManagerService
      .getOverlappingDossiers([...specialClosingHour.fitterIdList], {
        locations: locations as EventLocationInput[],
        periods: periods.map((period) => ({
          start: dayjs(period.startTime).format(),
          end: dayjs(period.endTime).format(),
        })),
      })
      .subscribe(
        (dossiers) => {
          this.overlapDossiers = dossiers;
          this.changeDetectorRef.markForCheck();
        },
        (error: HttpErrorResponse) => {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningFormEvent.getOverlapDossiersErr'
            )
          );

          console.error(error);
        }
      );
  }

  setSpecialClosingHoursExtreme() {
    const fitterTimezone = this.getTimezone();
    const specialClosingHour = this.form?.getRawValue();
    if (specialClosingHour?.allDay === true) {
      const startDate = dayjs(specialClosingHour.startDate ?? null)?.tz(
        fitterTimezone
      );
      const endDate = dayjs(specialClosingHour.endDate ?? null)?.tz(
        fitterTimezone
      );
      if (startDate?.isValid() && !endDate?.isValid()) {
        specialClosingHour.startDate = startDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(0)
          ?.minute(0)
          ?.format();
        specialClosingHour.endDate = startDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(23)
          ?.minute(59)
          ?.format();
      } else if (!startDate?.isValid() && !endDate?.isValid()) {
        specialClosingHour.startDate = dayjs()
          .tz(fitterTimezone)
          .hour(0)
          ?.minute(0)
          ?.format();
        specialClosingHour.endDate = dayjs()
          .tz(fitterTimezone)
          .hour(23)
          ?.minute(59)
          ?.format();
      } else if (startDate?.isValid() && endDate?.isValid()) {
        specialClosingHour.startDate = startDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(0)
          ?.minute(0)
          ?.format();
        specialClosingHour.endDate = endDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(23)
          ?.minute(59)
          ?.format();
      } else if (!startDate?.isValid() && endDate?.isValid()) {
        specialClosingHour.startDate = endDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(0)
          ?.minute(0)
          ?.format();
        specialClosingHour.endDate = endDate
          ?.clone()
          ?.tz(fitterTimezone)
          ?.startOf('day')
          ?.hour(23)
          ?.minute(59)
          ?.format();
      }

      this.form.get('startDate')?.setValue(specialClosingHour?.startDate);
      this.form.get('endDate')?.setValue(specialClosingHour?.endDate);

      this.form.updateValueAndValidity();
    }
  }

  onOverlappingDossierClick(dossierId: string) {
    this.router.goToPlanningNewTab(dossierId, false);
  }

  getDateValueFromInputEvent(event): ConfigType {
    if (typeof event === 'string') {
      return event;
    }

    if ('value' in event) {
      return event.value;
    }

    if ('target' in event) {
      return event.target.value;
    }

    return event;
  }

  displayLocationChoice() {
    return this.eventIsExceptionalClosing() || this.eventIsGeneric();
  }

  displayResourceChoice() {
    return !this.eventIsExceptionalClosing() && !this.eventIsUnavailability();
  }

  onStartDateChange(newDateEvent) {
    const fitterTimezone = this.getTimezone();
    let newDateMoment = dayjs(this.getDateValueFromInputEvent(newDateEvent)).tz(
      fitterTimezone,
      true
    );
    if (!newDateMoment?.isValid()) {
      return;
    }

    const currentFormDate = this.form.get('startDate').value;
    if (currentFormDate) {
      const currentMomentDate = dayjs(currentFormDate).tz(fitterTimezone);

      newDateMoment = newDateMoment
        .hour(currentMomentDate.hour())
        .minute(currentMomentDate.minute());
    }

    const currentEndDate = this.form.get('endDate').value;
    if (
      !!currentEndDate &&
      dayjs(currentEndDate)?.tz(fitterTimezone)?.isSameOrBefore(newDateMoment)
    ) {
      this.form
        .get('endDate')
        .setValue(newDateMoment?.add(1, 'hour')?.format());
    }

    this.form.get('startDate').setValue(newDateMoment.format());
    this.form.updateValueAndValidity();
    this.setSpecialClosingHoursExtreme();
    this.changeDetectorRef.markForCheck();
    this.checkAndUpdateOverlapDossier();
  }

  onEndDateChange(newDateEvent) {
    const fitterTimezone = this.getTimezone();
    let newDateMoment = dayjs(this.getDateValueFromInputEvent(newDateEvent)).tz(
      fitterTimezone,
      true
    );
    if (!newDateMoment?.isValid()) {
      return;
    }

    const currentEndFormDate = this.form.get('endDate').value;
    const currentMomentEndDate = dayjs(currentEndFormDate ?? null).tz(
      fitterTimezone
    );
    if (!!currentEndFormDate && currentMomentEndDate?.isValid()) {
      newDateMoment = newDateMoment
        ?.hour(currentMomentEndDate.hour())
        ?.minute(currentMomentEndDate.minute());
    }
    const currentStartDate = this.form.get('startDate').value;
    const currentStartMomentDate = dayjs(currentStartDate ?? null)?.tz(
      fitterTimezone
    );
    if (
      currentStartDate &&
      currentStartMomentDate?.isValid() &&
      currentStartMomentDate?.isSameOrAfter(newDateMoment)
    ) {
      newDateMoment = currentStartMomentDate?.add(1, 'hour');
    }

    this.form.get('endDate').setValue(newDateMoment.format());
    this.form.updateValueAndValidity();
    this.setSpecialClosingHoursExtreme();
    this.changeDetectorRef.markForCheck();
    this.checkAndUpdateOverlapDossier();
  }

  onRecurrentEndDateChange(dateChangeEvent: MatDatepickerInputEvent<Dayjs>) {
    if (dateChangeEvent) {
      this.form
        .get('recurrence')
        ?.get('endDate')
        ?.setValue(dateChangeEvent.value.format());
    }
  }

  onStartTimeChange(newTimeEvent) {
    const fitterTimezone = this.getTimezone();
    let newTimeMoment = dayjs(
      this.getDateValueFromInputEvent(newTimeEvent),
      'HH:mm'
    ).tz(fitterTimezone);
    if (!newTimeMoment?.isValid()) {
      return;
    }

    const currentFormStartDate = this.form.get('startDate').value;
    const currentFormEndDate = this.form.get('endDate').value;
    if (currentFormStartDate) {
      const currentMomentDate = dayjs(currentFormStartDate).tz(fitterTimezone);

      newTimeMoment = newTimeMoment
        .year(currentMomentDate.year())
        .month(currentMomentDate.month())
        .date(currentMomentDate.date());
    } else if (currentFormEndDate) {
      const currentMomentEndDate = dayjs(currentFormEndDate).tz(fitterTimezone);

      newTimeMoment = newTimeMoment
        .year(currentMomentEndDate.year())
        .month(currentMomentEndDate.month())
        .date(currentMomentEndDate.date());
    }

    this.form.get('startDate').setValue(newTimeMoment.format());

    if (this.form.get('type')?.value === 'SPECIAL_CLOSING') {
      this.form.get('allDay')?.setValue(false);
    }

    this.form.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
    this.checkAndUpdateOverlapDossier();
  }

  isLeaveType() {
    return this.form?.get('type')?.value?.includes('LEAVE');
  }

  onEndTimeChange(newTimeEvent) {
    const fitterTimezone = this.getTimezone();
    let newTimeMoment = dayjs(
      this.getDateValueFromInputEvent(newTimeEvent),
      'HH:mm'
    ).tz(fitterTimezone);
    if (!newTimeMoment?.isValid()) {
      return;
    }

    const currentFormEndDate = this.form.get('endDate').value;
    const currentFormStartDate = this.form.get('startDate').value;
    const currentMomentStartDate =
      dayjs(currentFormStartDate).tz(fitterTimezone);
    const currentMomentEndDate = dayjs(currentFormEndDate).tz(fitterTimezone);
    if (currentFormEndDate) {
      newTimeMoment = newTimeMoment
        .year(currentMomentEndDate.year())
        .month(currentMomentEndDate.month())
        .date(currentMomentEndDate.date());
    } else if (currentFormStartDate) {
      newTimeMoment = newTimeMoment
        .year(currentMomentStartDate.year())
        .month(currentMomentStartDate.month())
        .date(currentMomentStartDate.date());
    }

    if (this.form.get('type')?.value === 'SPECIAL_CLOSING') {
      this.form.get('allDay')?.setValue(false);
    }
    this.form.get('endDate').setValue(newTimeMoment.format());
    this.setSpecialClosingHoursExtreme();

    this.form.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
    this.checkAndUpdateOverlapDossier();
  }

  onFitterSelect(fitterId: string, keepHours = false) {
    const fitterIds = this.form?.getRawValue()?.fitterIdList;

    let usedFitterId: string = fitterId || null;

    if (fitterIds?.length === 1) {
      this.fitter = this.fitters?.find((fitter) => fitter?.id === fitterId);

      if (this.fitter) {
        usedFitterId = this.fitter.id;

        const startDate = this.form.get('startDate')?.value;
        if (startDate)
          this.form
            .get('startDate')
            .setValue(
              dayjs(startDate).tz(this.fitter.timezone, keepHours).format()
            );
        const endDate = this.form.get('endDate')?.value;
        if (endDate)
          this.form
            .get('endDate')
            .setValue(
              dayjs(endDate).tz(this.fitter.timezone, keepHours).format()
            );

        this.checkAndUpdateOverlapDossier();
      }
    }

    if (usedFitterId) {
      this.form.get('fitterIdList').setValue(merge(fitterIds, [usedFitterId]));
    }
  }

  onSelectSuggestionEvery(selectEvent: number) {
    this.form.get('recurrence')?.get('every')?.setValue(selectEvent);
  }

  getFormattedDate(formAttrDate: string, format?: string) {
    const fitterTimezone = this.getTimezone();
    const dateValue = this.form.get(formAttrDate).value;
    if (!dateValue || !dayjs(dateValue)?.isValid()) {
      return null;
    }
    return dayjs(dateValue)?.tz(fitterTimezone)?.format(format);
  }

  getFormattedRecurrentEndDate() {
    const fitterTimezone = this.getTimezone();
    const dateValue = this.form.get('recurrence')?.get('endDate').value;
    if (!dateValue) {
      return null;
    }
    return dayjs(dateValue)?.tz(fitterTimezone)?.format('DD/MM/YYYY');
  }

  getFormattedMomentDate(formAttrDate: string) {
    const fitterTimezone = this.getTimezone();
    const dateValue = this.form.get(formAttrDate).value
      ? dayjs(this.form.get(formAttrDate).value)
      : null;
    if (!dateValue || !dateValue?.isValid()) {
      return null;
    }

    return dateValue?.tz(fitterTimezone);
  }

  getFitterSelectedValue(fitterIds: string[]) {
    const fitters = this.fitters?.filter((fitter) =>
      fitterIds?.includes(fitter?.id)
    );
    if (fitters?.length) {
      return combineLatest(
        fitters.map((fitter) =>
          this.fitterNameDisplayService.getFitterNameTranslation(fitter)
        )
      );
    }

    return of(['-']);
  }

  getFitterResources() {
    const fitterIdList = this.form.get('fitterIdList').value;
    if (fitterIdList.length > 1) {
      return [];
    }

    const fitter = this.fitters.find((fitter) => fitter.id === fitterIdList[0]);
    return fitter
      ? fitter?.resources
          ?.filter((resource) => resource?.active)
          .map((resource) => resource?.name)
      : [];
  }

  getFitterInterventionVehicles() {
    const fitterIdList = this.form.get('fitterIdList').value;
    if (fitterIdList.length > 1) {
      return [];
    }

    const fitter = this.fitters.find((fitter) => fitter.id === fitterIdList[0]);
    return fitter
      ? fitter?.interventionVehicles
          ?.filter((interventionVehicle) => interventionVehicle?.active)
          .map((interventionVehicle) => interventionVehicle?.name)
      : [];
  }

  getFitterReplacementVehicles() {
    const fitterIdList = this.form.get('fitterIdList').value;
    if (fitterIdList.length > 1) {
      return [];
    }

    const fitter: Fitter = this.fitters.find(
      (fitter) => fitter.id === fitterIdList[0]
    );
    return fitter
      ? fitter?.vehicles
          ?.filter((vehicle) => vehicle?.active)
          .map((vehicle) => vehicle?.name)
      : [];
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  areTimeSelected() {
    return (
      !this.hasRequiredValidator(this.form.get('startDate')) &&
      !this.hasRequiredValidator(this.form.get('endDate'))
    );
  }

  isEndTimeValid() {
    return (
      !this.form.get('startDate')?.value ||
      !this.form.get('startDate')?.value ||
      dayjs(this.form.get('startDate')?.value)?.isBefore(
        dayjs(this.form.get('endDate')?.value)
      )
    );
  }

  isEventTimeValid(event: AgendaEvent) {
    return (
      dayjs(event?.startTime).isValid() &&
      dayjs(event?.endTime).isValid() &&
      (!this.planningCalendarService.isEventRecurrent(event) ||
        (dayjs(event?.recurrent?.startDate).isValid() &&
          (dayjs(event?.recurrent?.endDate).isValid() ||
            event?.recurrent?.endDate == null)))
    );
  }
}
